import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Guest } from '../types';
import { downloadCsv } from '../utils/csv';

// Firestore Timestamp → milliseconds (0 if missing)
const createdMillis = (g: Guest): number => {
  const c = g.createdAt;
  if (!c) return 0;
  if (typeof c.toMillis === 'function') return c.toMillis();
  if (typeof (c as any).seconds === 'number') return (c as any).seconds * 1000;
  return 0;
};

const formatCreated = (g: Guest): string => {
  const ms = createdMillis(g);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const TentativePage = () => {
  const authenticated = localStorage.getItem('dashboard_auth') === 'true';
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [promoting, setPromoting] = useState<string | null>(null);
  const [markingSent, setMarkingSent] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticated) return;
    const loadGuests = async () => {
      setLoading(true);
      setError('');
      try {
        const snapshot = await getDocs(collection(db, 'guests'));
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Guest, 'id'>),
        })) as Guest[];
        setGuests(items);
      } catch (e) {
        console.error(e);
        setError('Unable to load tentative guests.');
      } finally {
        setLoading(false);
      }
    };
    loadGuests();
  }, [authenticated]);

  // Anyone reaching this page must already be signed in on the dashboard.
  if (!authenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const promote = async (id: string) => {
    setPromoting(id);
    try {
      await updateDoc(doc(db, 'guests', id), { tentative: false });
      // Remove from this list locally — they now belong to the main dashboard.
      setGuests((prev) => prev.filter((g) => g.id !== id));
    } catch (e) {
      console.error(e);
      setError('Unable to move that guest. Please try again.');
    } finally {
      setPromoting(null);
    }
  };

  const toggleSent = async (id: string, current: boolean) => {
    const next = !current;
    if (current && !window.confirm('Mark this guest as NOT sent?')) return;
    setMarkingSent(id);
    try {
      await updateDoc(doc(db, 'guests', id), {
        inviteSent: next,
        ...(next ? { inviteSentAt: serverTimestamp() } : {}),
      });
      setGuests((prev) => prev.map((g) => (g.id === id ? { ...g, inviteSent: next } : g)));
    } catch (e) {
      console.error(e);
      setError('Unable to update sent status. Please try again.');
    } finally {
      setMarkingSent(null);
    }
  };

  const tentativeGuests = guests
    .filter((g) => g.tentative)
    .sort((a, b) => createdMillis(a) - createdMillis(b));

  const handleExport = () => {
    const headers = ['Name', 'ID', 'Added', 'Invite Link', 'Sent', 'Invited', 'Status', 'Coming', 'Contact'];
    const rows = tentativeGuests.map((g) => [
      g.name,
      g.id,
      formatCreated(g),
      `https://sadiqweddings.com/suha-and-hasan/${g.id}`,
      g.inviteSent ? 'Yes' : 'No',
      g.invitedGuests,
      g.rsvpStatus,
      g.comingCount,
      g.email || g.phone || '',
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`tentative-guests-${stamp}.csv`, headers, rows);
  };

  return (
    <div className="invite-background">
      <div className="invite-content">
        <div className="rsvp-form">
          <h1 className="rsvp-title" style={{ textAlign: 'center' }}>Tentative Guests</h1>
          <h2 className="rsvp-title" style={{ textAlign: 'center', fontSize: '1rem', marginTop: '-0.5rem' }}>
            "Maybe" list — not counted or messaged until moved
          </h2>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap', margin: '0.5rem 0 1rem' }}>
            <Link
              to="/dashboard"
              className="dashboard-filter-btn"
              style={{ textDecoration: 'none', display: 'inline-block' }}
            >
              ← Back to dashboard
            </Link>
            {tentativeGuests.length > 0 && (
              <button
                className="dashboard-filter-btn"
                onClick={handleExport}
                title="Export tentative guests to CSV"
              >
                Export CSV
              </button>
            )}
          </div>

          {loading && <div>Loading tentative guests...</div>}
          {error && <div className="rsvp-error">{error}</div>}

          {!loading && !error && tentativeGuests.length === 0 && (
            <div style={{ textAlign: 'center', color: '#a09070', margin: '1rem 0' }}>
              No tentative guests right now.
            </div>
          )}

          {!loading && !error && tentativeGuests.length > 0 && (
            <div className="dashboard-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th className="col-id">ID</th>
                    <th>Added</th>
                    <th>Invite Link</th>
                    <th>Sent</th>
                    <th>Invited</th>
                    <th>Status</th>
                    <th>Coming</th>
                    <th>Contact</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tentativeGuests.map((guest) => (
                    <tr key={guest.id}>
                      <td>{guest.name}</td>
                      <td className="col-id" style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#a09070' }}>{guest.id}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{formatCreated(guest)}</td>
                      <td>
                        <a
                          href={`https://sadiqweddings.com/suha-and-hasan/${guest.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#bfa14a', fontFamily: 'Playfair Display, serif', fontSize: '0.85rem' }}
                        >
                          Open invite
                        </a>
                      </td>
                      <td>
                        <button
                          className={`dashboard-filter-btn${guest.inviteSent ? ' active' : ''}`}
                          onClick={() => toggleSent(guest.id, !!guest.inviteSent)}
                          disabled={markingSent === guest.id}
                          style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', padding: '0.2em 0.6em' }}
                          title={guest.inviteSent ? 'Sent — click to un-mark' : 'Click to mark as sent'}
                        >
                          {markingSent === guest.id ? '…' : guest.inviteSent ? '✅ Sent' : 'Mark sent'}
                        </button>
                      </td>
                      <td>{guest.invitedGuests}</td>
                      <td>{guest.rsvpStatus}</td>
                      <td>{guest.comingCount}</td>
                      <td>{guest.email || guest.phone || '—'}</td>
                      <td>
                        <button
                          className="dashboard-filter-btn active"
                          onClick={() => promote(guest.id)}
                          disabled={promoting === guest.id}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {promoting === guest.id ? 'Moving...' : 'Move to dashboard'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TentativePage;
